import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useRegionalBackend } from "../../context/RegionalBackendContext";
import { recordRegionalLegalAction } from "../../utils/backend/pdxBackendClient";
import { LEGAL_SUBMISSION_ACTIONS } from "../../utils/legal/legalStatus";

/**
 * Shared logic for the app-wide legal-update flow.
 *
 * `outstandingDocuments` are the documents the signed-in user still has a
 * required action on — keyed off the server-computed status (`pending` OR
 * `declined`) with no valid current acceptance, NOT `allowedActions` (which is
 * populated for any acknowledgement/consent document regardless of whether it
 * has been accepted). A declined document stays outstanding so the user remains
 * blocked until they agree. `hasBlockingUpdate` mirrors the backend
 * `appUseBlocked` flag so the overlay only blocks for genuinely app-blocking
 * updates (e.g. updated Terms), matching the dashboard. `acknowledgeAll` records
 * the satisfying action and refreshes; `declineAll` records a `declined` action
 * for audit before the caller signs the user out.
 *
 * Used by both the blocking overlay and the in-document "Agree and continue"
 * buttons so they behave identically.
 */
export function useLegalUpdate() {
  const { t } = useTranslation(["common", "legal"]);
  const { legalStatus, refresh, profile, applyLegalStatus } =
    useRegionalBackend();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const outstandingDocuments = useMemo(() => {
    const documents = legalStatus?.documents
      ? Object.values(legalStatus.documents)
      : [];

    return documents.filter(
      (document) =>
        document &&
        document.currentAcceptanceValid !== true &&
        (document.status === "pending" || document.status === "declined"),
    );
  }, [legalStatus]);

  const resolveAction = (document) => {
    const allowed = document.allowedActions || [];

    if (allowed.includes(LEGAL_SUBMISSION_ACTIONS.ACKNOWLEDGED)) {
      return LEGAL_SUBMISSION_ACTIONS.ACKNOWLEDGED;
    }

    if (allowed.includes(LEGAL_SUBMISSION_ACTIONS.CONSENTED)) {
      return LEGAL_SUBMISSION_ACTIONS.CONSENTED;
    }

    return LEGAL_SUBMISSION_ACTIONS.PRESENTED;
  };

  const acknowledgeAll = useCallback(async () => {
    if (outstandingDocuments.length === 0) {
      return true;
    }

    setSubmitting(true);
    setError("");

    try {
      let nextLegalStatus = null;

      for (const document of outstandingDocuments) {
        const response = await recordRegionalLegalAction({
          documentType: document.documentType,
          version: document.version,
          locale: document.locale || legalStatus?.locale || "en-GB",
          action: resolveAction(document),
          actionMethod: "in_app",
          clinicStudyId:
            legalStatus?.clinicStudyId || profile?.clinicStudyId || null,
        });

        if (response?.legalStatus) {
          nextLegalStatus = response.legalStatus;
        }
      }

      if (nextLegalStatus) {
        applyLegalStatus?.(nextLegalStatus);
        void Promise.resolve(refresh?.()).catch(() => {});
      } else {
        await refresh?.();
      }
      return true;
    } catch {
      setError(
        t("overlay.error", {
          ns: "legal",
          defaultValue: "We couldn't save your response. Please try again.",
        }),
      );
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [
    outstandingDocuments,
    legalStatus,
    profile,
    refresh,
    applyLegalStatus,
    t,
  ]);

  // Records a `declined` action for outstanding documents that allow it, for
  // the audit trail, before the caller signs the user out. Best-effort: the
  // user is being signed out regardless, and a declined document remains
  // outstanding so they stay blocked until they agree on a later sign-in.
  const declineAll = useCallback(async () => {
    const declinable = outstandingDocuments.filter((document) =>
      (document.allowedActions || []).includes(
        LEGAL_SUBMISSION_ACTIONS.DECLINED,
      ),
    );

    for (const document of declinable) {
      try {
        await recordRegionalLegalAction({
          documentType: document.documentType,
          version: document.version,
          locale: document.locale || legalStatus?.locale || "en-GB",
          action: LEGAL_SUBMISSION_ACTIONS.DECLINED,
          actionMethod: "in_app",
          clinicStudyId:
            legalStatus?.clinicStudyId || profile?.clinicStudyId || null,
        });
      } catch {
        // Ignore — this is a best-effort audit record on sign-out.
      }
    }
  }, [outstandingDocuments, legalStatus, profile]);

  return {
    outstandingDocuments,
    hasBlockingUpdate: legalStatus?.appUseBlocked === true,
    submitting,
    error,
    acknowledgeAll,
    declineAll,
  };
}
